/**
 * @fileoverview Рендер поля Canvas 2D (мир 1600×900 → cover fit).
 */

import { APRON, HOLD_SHORT, H, TAXI_Y, TERMINAL, W } from "../constants/world";
import { canLand, canTakeoff } from "../commands/ops";
import { frontStage } from "../state/createGame";
import type { CameraState, GameState, Plane, Runway } from "../state/types";
import { clamp, lerp, rnd } from "../util/math";
import { TrackLayer } from "./TrackLayer";

export interface ViewState {
  scale: number;
  ox: number;
  oy: number;
  dpr: number;
}

interface Flake {
  x: number;
  y: number;
  z: number;
  r: number;
  s: number;
}

type Rgb = readonly [number, number, number];

function mixRgb(a: Rgb, b: Rgb, t: number): string {
  const k = clamp(t, 0, 1);
  return `rgb(${Math.round(lerp(a[0], b[0], k))},${Math.round(lerp(a[1], b[1], k))},${Math.round(lerp(a[2], b[2], k))})`;
}

/**
 * Владеет основным canvas `#game`, оверлеем следов и виджетами радара / ветра.
 */
export class CanvasRenderer {
  readonly tracks = new TrackLayer();
  view: ViewState = { scale: 1, ox: 0, oy: 0, dpr: 1 };
  private readonly cv: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly rcv: HTMLCanvasElement | null;
  private readonly rctx: CanvasRenderingContext2D | null;
  private readonly wcv: HTMLCanvasElement | null;
  private readonly wctx: CanvasRenderingContext2D | null;
  private readonly wrap: HTMLElement;
  private readonly flakes: Flake[] = [];
  private ambientNight = 1;
  private ro: ResizeObserver | null = null;

  constructor(root: ParentNode) {
    const cv = root.querySelector<HTMLCanvasElement>("#game");
    const wrap = root.querySelector<HTMLElement>("#canvasWrap");
    if (!cv || !wrap) throw new Error("CanvasRenderer: #game / #canvasWrap missing");
    const ctx = cv.getContext("2d");
    if (!ctx) throw new Error("2d context unavailable");
    this.cv = cv;
    this.ctx = ctx;
    this.wrap = wrap;
    this.rcv = root.querySelector("#radar");
    this.wcv = root.querySelector("#wind");
    this.rctx = this.rcv?.getContext("2d") ?? null;
    this.wctx = this.wcv?.getContext("2d") ?? null;
    for (let i = 0; i < 340; i++) {
      const z = Math.random();
      this.flakes.push({
        x: Math.random() * W,
        y: Math.random() * H,
        z,
        r: 0.7 + z * 2.6,
        s: 40 + z * 150,
      });
    }
  }

  /** Подключить наблюдатели resize. */
  attach(): void {
    this.resize();
    window.addEventListener("resize", this.resize);
    if (typeof ResizeObserver !== "undefined") {
      this.ro = new ResizeObserver(() => this.resize());
      this.ro.observe(this.wrap);
    }
  }

  dispose(): void {
    window.removeEventListener("resize", this.resize);
    this.ro?.disconnect();
  }

  readonly resize = (): void => {
    const w = this.wrap.clientWidth;
    const h = this.wrap.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.cv.width = Math.max(1, Math.round(w * dpr));
    this.cv.height = Math.max(1, Math.round(h * dpr));
    // cover: без черных полос, поле на всю высоту/ширину области карты
    const s = Math.max(w / W, h / H);
    this.view = { scale: s, ox: (w - W * s) / 2, oy: (h - H * s) / 2, dpr };
    this.sizeHud();
  };

  private sizeHud(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    for (const [el, fallback] of [
      [this.rcv, 128],
      [this.wcv, 96],
    ] as const) {
      if (!el) continue;
      const css = Math.max(
        40,
        Math.round(el.getBoundingClientRect().width || fallback),
      );
      const px = Math.max(1, Math.round(css * dpr));
      if (el.width !== px || el.height !== px) {
        el.width = px;
        el.height = px;
      }
      (el as HTMLCanvasElement & { _css?: number; _dpr?: number })._css = css;
      (el as HTMLCanvasElement & { _css?: number; _dpr?: number })._dpr = dpr;
    }
  }

  /** Координаты pointer-события → мировые. */
  worldPos(ev: PointerEvent, cam: CameraState): { x: number; y: number } {
    const r = this.cv.getBoundingClientRect();
    const zs = this.view.scale * cam.zoom;
    return {
      x: (ev.clientX - r.left - this.view.ox - cam.panX) / zs,
      y: (ev.clientY - r.top - this.view.oy - cam.panY) / zs,
    };
  }

  /** Полная отрисовка кадра. */
  paint(
    g: GameState | null,
    cam: CameraState,
    selected: Plane | null,
    dt: number,
    t: number,
  ): void {
    const { ctx, cv, view } = this;
    const dpr = view.dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = "#03060d";
    ctx.fillRect(0, 0, cv.width / dpr, cv.height / dpr);

    if (g) this.tracks.fade(dt);

    const sh = g ? g.shake * 8 : 0;
    const zs = view.scale * cam.zoom;
    const ox = view.ox + cam.panX + rnd(-sh, sh);
    const oy = view.oy + cam.panY + rnd(-sh, sh);
    ctx.setTransform(dpr * zs, 0, 0, dpr * zs, dpr * ox, dpr * oy);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, W, H);
    ctx.clip();

    this.drawField(t, g);
    if (g) for (const r of g.runways) this.drawRunway(r, t, g);
    if (g) this.drawHoldShorts(g, t);
    ctx.drawImage(this.tracks.canvas, 0, 0);

    if (g) {
      this.drawTargets(g, selected, t);
      this.drawClearingGear(g);
      const order = [...g.planes].sort((a, b) => a.alt - b.alt);
      for (const p of order) {
        if (!p.dead || p.crashed) this.drawPlane(p, t, g, selected);
      }
      ctx.save();
      for (const s of g.sparks) {
        ctx.globalAlpha = clamp(s.t, 0, 1);
        ctx.fillStyle = s.c;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.s, 0, 7);
        ctx.fill();
      }
      ctx.restore();
      this.drawFront(g, t);
      this.drawWeather(g, dt);
      this.drawFloats(g);
      this.drawHazards(g, t);
      if (g.flash > 0) {
        ctx.save();
        ctx.globalAlpha = g.flash * 0.25;
        ctx.fillStyle = "#ff2020";
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }
    } else {
      this.drawWeather(null, dt);
    }

    ctx.restore();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.drawRadar(g, t);
    this.drawWind(g);
  }

  private rr(
    x: number,
    y: number,
    w: number,
    h: number,
    r: number,
  ): void {
    const c = this.ctx;
    c.beginPath();
    c.moveTo(x + r, y);
    c.lineTo(x + w - r, y);
    c.quadraticCurveTo(x + w, y, x + w, y + r);
    c.lineTo(x + w, y + h - r);
    c.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    c.lineTo(x + r, y + h);
    c.quadraticCurveTo(x, y + h, x, y + h - r);
    c.lineTo(x, y + r);
    c.quadraticCurveTo(x, y, x + r, y);
    c.closePath();
  }

  private lightDot(
    x: number,
    y: number,
    r: number,
    col: string,
    glow: number,
    gcol: string,
  ): void {
    const c = this.ctx;
    if (glow) {
      const g = c.createRadialGradient(x, y, 0, x, y, glow);
      g.addColorStop(0, gcol);
      g.addColorStop(1, "rgba(0,0,0,0)");
      c.fillStyle = g;
      c.beginPath();
      c.arc(x, y, glow, 0, 7);
      c.fill();
    }
    c.fillStyle = col;
    c.beginPath();
    c.arc(x, y, r, 0, 7);
    c.fill();
  }

  private drawField(t: number, g: GameState | null): void {
    const c = this.ctx;
    const light = this.dayLight(g);
    this.ambientNight = light.night;
    const bg = c.createLinearGradient(0, 0, 0, H);
    bg.addColorStop(0, light.skyTop);
    bg.addColorStop(0.35, light.skyMid);
    bg.addColorStop(1, light.ground);
    c.fillStyle = bg;
    c.fillRect(0, 0, W, H);

    // Заснеженное поле (пятна)
    c.save();
    c.globalAlpha = 0.5;
    for (let i = 0; i < 26; i++) {
      const x = (i * 211 + 90) % W;
      const y = 170 + ((i * 173) % (H - 200));
      const gg = c.createRadialGradient(x, y, 0, x, y, 120 + (i % 4) * 60);
      gg.addColorStop(0, "rgba(150,190,235,.05)");
      gg.addColorStop(1, "rgba(0,0,0,0)");
      c.fillStyle = gg;
      c.beginPath();
      c.arc(x, y, 120 + (i % 4) * 60, 0, 7);
      c.fill();
    }
    c.restore();

    // Небо / горизонт
    const sg = c.createLinearGradient(0, 0, 0, 190);
    sg.addColorStop(0, light.horizon);
    sg.addColorStop(1, "rgba(10,20,36,0)");
    c.fillStyle = sg;
    c.fillRect(0, 0, W, 190);
    if (light.warm > 0.01) {
      const sunX = light.phase < 0.45 ? 250 : 1350;
      const sun = c.createRadialGradient(sunX, 145, 0, sunX, 145, 330);
      sun.addColorStop(0, `rgba(255,184,116,${0.22 * light.warm})`);
      sun.addColorStop(0.18, `rgba(255,132,88,${0.11 * light.warm})`);
      sun.addColorStop(1, "rgba(255,120,80,0)");
      c.fillStyle = sun;
      c.fillRect(0, 0, W, 340);
    }
    c.strokeStyle = `rgba(140,190,235,${0.09 + light.night * 0.07})`;
    c.lineWidth = 1;
    c.beginPath();
    c.moveTo(0, 176);
    c.lineTo(W, 176);
    c.stroke();

    // Зона ожидания (воздух)
    c.save();
    c.strokeStyle = "rgba(95,215,255,.10)";
    c.setLineDash([10, 12]);
    c.lineWidth = 1.4;
    c.beginPath();
    c.ellipse(800, 100, 400, 46, 0, 0, 7);
    c.stroke();
    c.beginPath();
    c.ellipse(800, 100, 345, 40, 0, 0, 7);
    c.stroke();
    c.setLineDash([]);
    c.fillStyle = "rgba(95,215,255,.30)";
    c.font = "600 11px ui-monospace,monospace";
    c.textAlign = "left";
    c.fillText("ЗОНА ОЖИДАНИЯ · HOLDING", 26, 26);
    c.restore();

    const gates = g?.gates ?? [
      { x: 470, y: 672, name: "A1", plane: null },
      { x: 672, y: 672, name: "A2", plane: null },
      { x: 874, y: 672, name: "B1", plane: null },
      { x: 1076, y: 672, name: "B2", plane: null },
    ];
    const rwyY = [g?.runways[0]?.y ?? 252, g?.runways[1]?.y ?? 452];

    // Рулежка
    c.strokeStyle = "#121c2c";
    c.lineWidth = 44;
    c.lineCap = "round";
    c.beginPath();
    c.moveTo(150, TAXI_Y);
    c.lineTo(1440, TAXI_Y);
    c.stroke();

    // Соединения к гейтам / полосам
    c.lineWidth = 30;
    c.beginPath();
    for (const gt of gates) {
      c.moveTo(gt.x, TAXI_Y);
      c.lineTo(gt.x, gt.y + 8);
    }
    HOLD_SHORT.forEach((h, i) => {
      c.moveTo(h.x, TAXI_Y);
      c.lineTo(h.x, rwyY[i]!);
    });
    for (const a of APRON) {
      c.moveTo(a.x, TAXI_Y);
      c.lineTo(a.x, a.y);
    }
    c.moveTo(1300, 660);
    c.lineTo(1370, 730);
    c.lineTo(1300, 800);
    c.lineTo(1215, 800);
    c.moveTo(320, TAXI_Y);
    c.lineTo(320, rwyY[1]!);
    c.moveTo(1240, TAXI_Y);
    c.lineTo(1240, rwyY[1]!);
    c.moveTo(1240, rwyY[1]!);
    c.lineTo(1240, rwyY[0]!);
    c.moveTo(320, rwyY[1]!);
    c.lineTo(320, rwyY[0]!);
    c.stroke();

    // Осевые рулежек
    c.strokeStyle = "rgba(255,205,70,.42)";
    c.lineWidth = 2;
    c.setLineDash([16, 14]);
    c.beginPath();
    c.moveTo(150, TAXI_Y);
    c.lineTo(1440, TAXI_Y);
    for (const gt of gates) {
      c.moveTo(gt.x, TAXI_Y);
      c.lineTo(gt.x, gt.y + 6);
    }
    HOLD_SHORT.forEach((h, i) => {
      c.moveTo(h.x, TAXI_Y);
      c.lineTo(h.x, rwyY[i]!);
    });
    c.stroke();
    c.setLineDash([]);
    c.lineCap = "butt";

    // Терминал
    const T = TERMINAL;
    c.save();
    c.shadowColor = `rgba(255,190,90,${0.06 + 0.14 * light.night})`;
    c.shadowBlur = 40;
    const tg = c.createLinearGradient(0, T.y, 0, T.y + T.h);
    tg.addColorStop(0, "#22304a");
    tg.addColorStop(1, "#131d2e");
    c.fillStyle = tg;
    this.rr(T.x, T.y, T.w, T.h, 16);
    c.fill();
    c.restore();
    c.strokeStyle = "rgba(150,200,255,.20)";
    c.lineWidth = 1.5;
    this.rr(T.x, T.y, T.w, T.h, 16);
    c.stroke();
    // Крыша со снегом
    c.fillStyle = "rgba(220,238,255,.16)";
    this.rr(T.x + 6, T.y + 4, T.w - 12, 16, 8);
    c.fill();
    // Панорамное остекление
    for (let j = 0; j < 2; j++) {
      const y = T.y + 30 + j * 44;
      c.fillStyle = "rgba(18,28,44,.85)";
      this.rr(T.x + 18, y - 4, T.w - 36, 32, 6);
      c.fill();
      for (let i = 0; i < 22; i++) {
        const x = T.x + 26 + i * ((T.w - 52) / 22);
        const f = 0.45 + 0.55 * Math.sin(t * 0.9 + i * 2.3 + j * 1.7);
        const warm = ((i + j) % 7 === 0 ? 0.16 : 0.3 + 0.42 * f) * (0.28 + 0.72 * light.night);
        c.fillStyle = `rgba(255,201,124,${warm})`;
        this.rr(x, y, (T.w - 52) / 22 - 6, 24, 3);
        c.fill();
      }
      c.fillStyle = "rgba(255,225,180,.10)";
      this.rr(T.x + 18, y - 4, T.w - 36, 7, 4);
      c.fill();
    }
    c.fillStyle = "rgba(255,220,160,.75)";
    c.font = "700 15px ui-monospace,monospace";
    c.textAlign = "center";
    c.fillText("T E R M I N A L", T.x + T.w / 2, T.y + T.h - 14);
    // Теплое свечение
    const wg = c.createRadialGradient(
      T.x + T.w / 2,
      T.y + 40,
      10,
      T.x + T.w / 2,
      T.y + 40,
      460,
    );
    wg.addColorStop(0, `rgba(255,190,110,${0.025 + 0.075 * light.night})`);
    wg.addColorStop(1, "rgba(0,0,0,0)");
    c.fillStyle = wg;
    c.beginPath();
    c.arc(T.x + T.w / 2, T.y + 40, 460, 0, 7);
    c.fill();

    // Гейты (A1–B2 одинаковые по скорости; подпись — занятость / остаток обслуживания)
    for (const gt of gates) {
      const occ = gt.plane && !gt.plane.dead ? gt.plane : null;
      const free = !occ;
      // Телетрап
      c.strokeStyle = "rgba(160,200,250,.35)";
      c.lineWidth = 9;
      c.lineCap = "round";
      c.beginPath();
      c.moveTo(gt.x, T.y + 6);
      c.lineTo(gt.x, gt.y + 26);
      c.stroke();
      c.lineCap = "butt";
      // Площадка
      c.strokeStyle = free
        ? "rgba(126,224,138,.55)"
        : "rgba(255,200,97,.5)";
      c.lineWidth = 2;
      c.setLineDash([9, 7]);
      c.beginPath();
      c.arc(gt.x, gt.y, 40, 0, 7);
      c.stroke();
      c.setLineDash([]);
      c.fillStyle = free
        ? "rgba(126,224,138,.08)"
        : "rgba(255,200,97,.08)";
      c.beginPath();
      c.arc(gt.x, gt.y, 40, 0, 7);
      c.fill();
      c.fillStyle = free
        ? "rgba(126,224,138,.85)"
        : "rgba(255,200,97,.85)";
      c.font = "700 12px ui-monospace,monospace";
      c.textAlign = "center";
      c.fillText(gt.name, gt.x, gt.y + 52);
      c.font = "600 10px ui-monospace,monospace";
      c.fillStyle = free
        ? "rgba(160,190,220,.75)"
        : "rgba(255,210,140,.9)";
      let sub = "свободен";
      if (occ) {
        if (occ.status === "service") sub = `${Math.ceil(occ.svcLeft)}с`;
        else if (occ.status === "taxi_gate") sub = "едет";
        else if (occ.status === "ready") sub = "готов";
        else sub = occ.cs;
      }
      c.fillText(sub, gt.x, gt.y + 66);
      this.lightDot(
        gt.x - 30,
        gt.y - 26,
        2,
        "rgba(255,190,90,.9)",
        8,
        "rgba(255,180,80,.2)",
      );
      this.lightDot(
        gt.x + 30,
        gt.y - 26,
        2,
        "rgba(255,190,90,.9)",
        8,
        "rgba(255,180,80,.2)",
      );
    }

    // Зона ожидания перрона
    c.save();
    c.strokeStyle = "rgba(95,215,255,.18)";
    c.setLineDash([8, 8]);
    c.lineWidth = 1.5;
    this.rr(1170, 620, 270, 220, 14);
    c.stroke();
    c.setLineDash([]);
    c.fillStyle = "rgba(8,16,30,.72)";
    this.rr(1180, 608, 168, 18, 6);
    c.fill();
    c.fillStyle = "rgba(160,210,245,.85)";
    c.font = "700 10px ui-monospace,monospace";
    c.textAlign = "left";
    c.fillText("ОЖИДАНИЕ · без гейта", 1188, 621);
    c.restore();

    // Гараж техники
    c.fillStyle = "#16212f";
    this.rr(220, 660, 100, 180, 10);
    c.fill();
    c.strokeStyle = "rgba(150,200,255,.22)";
    this.rr(220, 660, 100, 180, 10);
    c.stroke();
    c.fillStyle = "rgba(220,238,255,.14)";
    this.rr(224, 662, 92, 12, 6);
    c.fill();
    c.fillStyle = "rgba(170,210,250,.65)";
    c.font = "600 10px ui-monospace,monospace";
    c.textAlign = "center";
    c.fillText("ГАРАЖ", 270, 856);

    this.spotlight(300, 700, t * 0.5, 0.9 * light.night);
    this.spotlight(1300, 700, -t * 0.42 + 2, 0.8 * light.night);
  }

  private dayLight(g: GameState | null): {
    phase: number;
    night: number;
    warm: number;
    skyTop: string;
    skyMid: string;
    ground: string;
    horizon: string;
  } {
    if (!g) {
      return {
        phase: 0.45,
        night: 1,
        warm: 0,
        skyTop: "#060d1b",
        skyMid: "#0a1526",
        ground: "#07101f",
        horizon: "rgba(24,48,86,.85)",
      };
    }
    const endlessLoop = (g.timeAlive % 720) / 360;
    const phase = g.cfg.endless
      ? endlessLoop <= 1 ? endlessLoop : 2 - endlessLoop
      : clamp(g.timeAlive / Math.max(1, g.cfg.time), 0, 1);
    const eveningTop: Rgb = [38, 25, 43];
    const eveningMid: Rgb = [31, 38, 58];
    const eveningGround: Rgb = [11, 19, 31];
    const nightTop: Rgb = [6, 13, 27];
    const nightMid: Rgb = [10, 21, 38];
    const nightGround: Rgb = [7, 16, 31];
    const dawnTop: Rgb = [87, 78, 99];
    const dawnMid: Rgb = [73, 88, 108];
    const dawnGround: Rgb = [29, 42, 54];
    const beforeNight = phase < 0.45;
    const k = beforeNight ? phase / 0.45 : (phase - 0.45) / 0.55;
    const night = beforeNight ? lerp(0.68, 1, k) : lerp(1, 0.28, k);
    const warm = beforeNight ? 1 - k : k;
    const fromTop = beforeNight ? eveningTop : nightTop;
    const toTop = beforeNight ? nightTop : dawnTop;
    const fromMid = beforeNight ? eveningMid : nightMid;
    const toMid = beforeNight ? nightMid : dawnMid;
    const fromGround = beforeNight ? eveningGround : nightGround;
    const toGround = beforeNight ? nightGround : dawnGround;
    const horizonAlpha = 0.22 + warm * 0.48;
    return {
      phase,
      night,
      warm,
      skyTop: mixRgb(fromTop, toTop, k),
      skyMid: mixRgb(fromMid, toMid, k),
      ground: mixRgb(fromGround, toGround, k),
      horizon: beforeNight
        ? `rgba(132,72,70,${horizonAlpha})`
        : `rgba(221,145,108,${horizonAlpha})`,
    };
  }

  private spotlight(x: number, y: number, a: number, p: number): void {
    const c = this.ctx;
    c.save();
    c.globalCompositeOperation = "lighter";
    c.translate(x, y);
    c.rotate(Math.sin(a) * 0.9 - Math.PI / 2);
    const g = c.createLinearGradient(0, 0, 0, -460);
    g.addColorStop(0, `rgba(160,210,255,${0.13 * p})`);
    g.addColorStop(1, "rgba(160,210,255,0)");
    c.fillStyle = g;
    c.beginPath();
    c.moveTo(0, 0);
    c.lineTo(-130, -460);
    c.lineTo(130, -460);
    c.closePath();
    c.fill();
    c.restore();
    this.lightDot(
      x,
      y,
      3.4,
      "rgba(200,230,255,.9)",
      16,
      "rgba(150,210,255,.25)",
    );
  }

  private edgeLights(r: Runway, t: number): void {
    const n = 26;
    const night = 0.25 + 0.75 * this.ambientNight;
    const alpha = (0.5 + 0.5 * Math.sin(t * 2.2)) * night;
    for (let i = 0; i <= n; i++) {
      const x = lerp(r.x1, r.x2, i / n);
      for (const dy of [-r.w / 2 - 6, r.w / 2 + 6]) {
        const on = i % 2 === 0 ? 1 : 0.55;
        this.lightDot(
          x,
          r.y + dy,
          2.2,
          `rgba(120,200,255,${0.55 * on * night})`,
          9,
          `rgba(90,180,255,${0.16 * on * alpha})`,
        );
      }
    }
    // Огни подхода (бегущая дорожка)
    const seq = (t * 2.4) % 1;
    for (let i = 0; i < 7; i++) {
      const x = r.x1 - 30 - i * 32;
      const f = ((i / 7) - seq + 1) % 1;
      const a = f < 0.18 ? 1 : 0.08;
      this.lightDot(
        x,
        r.y,
        2.6,
        `rgba(255,255,255,${a * 0.9})`,
        12,
        `rgba(200,230,255,${a * 0.25})`,
      );
    }
    // Торцевые
    for (let i = -1; i <= 1; i++) {
      this.lightDot(
        r.x1 - 6,
        r.y + i * (r.w / 2 - 6),
        3,
        "rgba(120,255,160,.9)",
        12,
        "rgba(80,255,150,.22)",
      );
      this.lightDot(
        r.x2 + 6,
        r.y + i * (r.w / 2 - 6),
        3,
        "rgba(255,90,90,.9)",
        12,
        "rgba(255,80,80,.22)",
      );
    }
  }

  private drawRunway(r: Runway, t: number, g: GameState): void {
    const c = this.ctx;
    const s = r.snow / 100;
    const y0 = r.y - r.w / 2;
    // Тень / основание
    c.fillStyle = "rgba(6,12,22,.85)";
    this.rr(r.x1 - 14, y0 - 14, r.x2 - r.x1 + 28, r.w + 28, 10);
    c.fill();
    // Асфальт
    const grd = c.createLinearGradient(0, y0, 0, y0 + r.w);
    grd.addColorStop(0, "#1b2637");
    grd.addColorStop(0.5, "#141d2b");
    grd.addColorStop(1, "#0f1725");
    c.fillStyle = grd;
    c.fillRect(r.x1, y0, r.x2 - r.x1, r.w);
    // Мокрые блики
    c.save();
    c.globalAlpha = 0.1 * (1 - s);
    for (let i = 0; i < 9; i++) {
      const x = r.x1 + ((i * 137 + r.id * 60) % (r.x2 - r.x1));
      const wet = c.createLinearGradient(x, y0, x + 90, y0 + r.w);
      wet.addColorStop(0, "rgba(150,210,255,0)");
      wet.addColorStop(0.5, "rgba(150,210,255,.9)");
      wet.addColorStop(1, "rgba(150,210,255,0)");
      c.fillStyle = wet;
      c.fillRect(x, y0, 90, r.w);
    }
    c.restore();
    // Разметка
    const mA = 0.85 * (1 - s * 0.82);
    c.strokeStyle = `rgba(235,245,255,${mA})`;
    c.lineWidth = 3.4;
    c.setLineDash([44, 34]);
    c.beginPath();
    c.moveTo(r.x1 + 120, r.y);
    c.lineTo(r.x2 - 120, r.y);
    c.stroke();
    c.setLineDash([]);
    c.fillStyle = `rgba(235,245,255,${mA})`;
    for (let i = 0; i < 6; i++) {
      const yy = y0 + 8 + i * ((r.w - 16) / 6);
      c.fillRect(r.x1 + 18, yy, 56, 5.5);
      c.fillRect(r.x2 - 74, yy, 56, 5.5);
    }
    c.save();
    c.globalAlpha = mA * 0.9;
    c.fillStyle = "#eaf3ff";
    c.font = "700 21px ui-monospace,Menlo,Consolas,monospace";
    c.textAlign = "center";
    c.fillText(r.id === 0 ? "09L · ГЛ" : "27R", r.x1 + 108, r.y + 7);
    c.fillText(r.id === 0 ? "27R" : "09L", r.x2 - 108, r.y + 7);
    c.restore();
    // Снег
    if (s > 0.02) {
      c.save();
      c.beginPath();
      c.rect(r.x1, y0, r.x2 - r.x1, r.w);
      c.clip();
      c.fillStyle = `rgba(214,232,252,${0.4 * s})`;
      c.fillRect(r.x1, y0, r.x2 - r.x1, r.w);
      c.fillStyle = `rgba(255,255,255,${0.16 * s})`;
      for (let i = 0; i < 40; i++) {
        const x = r.x1 + ((i * 73.7 + r.id * 41) % (r.x2 - r.x1));
        const yy = y0 + ((i * 29.3) % r.w);
        c.beginPath();
        c.ellipse(x, yy, 22 + (i % 5) * 9, 6 + (i % 3) * 3, 0, 0, 7);
        c.fill();
      }
      c.restore();
    }
    // Гололед — голубой блеск (не путать со снегом)
    const ice = r.ice || 0;
    if (ice > 6) {
      c.save();
      c.globalAlpha = 0.12 + Math.min(0.28, ice / 220);
      c.fillStyle = "#7ec8ff";
      c.fillRect(r.x1, y0, r.x2 - r.x1, r.w);
      c.restore();
    }
    this.edgeLights(r, t);
    // Риск посадки/взлета (пороги как в canLand / canTakeoff)
    const riskySnow = r.snow > 62;
    const riskyIce = ice > 40;
    if (!r.closed && (riskySnow || riskyIce)) {
      c.save();
      const pulse = 0.35 + 0.3 * Math.sin(t * 4.5);
      c.strokeStyle = `rgba(255,107,107,${pulse})`;
      c.lineWidth = 2.5;
      c.setLineDash([12, 10]);
      c.strokeRect(r.x1 + 2, y0 + 2, r.x2 - r.x1 - 4, r.w - 4);
      c.setLineDash([]);
      c.restore();
    }
    if (r.closed) {
      c.save();
      c.globalAlpha = 0.2 + 0.12 * Math.sin(t * 5);
      c.fillStyle = "#ffb020";
      c.fillRect(r.x1, y0, r.x2 - r.x1, r.w);
      c.restore();
      c.save();
      c.strokeStyle = `rgba(255,180,40,${0.55 + 0.35 * Math.sin(t * 5)})`;
      c.lineWidth = 3;
      c.setLineDash([16, 12]);
      c.strokeRect(r.x1, y0, r.x2 - r.x1, r.w);
      c.setLineDash([]);
      c.restore();
    }
    if ((r.reserveT || 0) > 0) {
      c.save();
      c.strokeStyle = `rgba(181,140,255,${0.55 + 0.4 * Math.sin(t * 4)})`;
      c.lineWidth = 3;
      c.setLineDash([10, 8]);
      c.strokeRect(r.x1 + 4, y0 + 4, r.x2 - r.x1 - 8, r.w - 8);
      c.setLineDash([]);
      c.fillStyle = "rgba(28,14,48,.82)";
      const lab = `РЕЗЕРВ ${r.reserveLabel || "MED"} · ${Math.ceil(r.reserveT)}с`;
      c.font = "800 13px ui-monospace,Menlo,monospace";
      c.textAlign = "center";
      const tw = c.measureText(lab).width + 16;
      this.rr(r.x1 + (r.x2 - r.x1) / 2 - tw / 2, y0 - 22, tw, 18, 6);
      c.fill();
      c.fillStyle = "#e0ccff";
      c.fillText(lab, r.x1 + (r.x2 - r.x1) / 2, y0 - 9);
      c.restore();
    }
    if (g.hazards.some((h) => h.r === r)) {
      c.save();
      c.strokeStyle = `rgba(255,60,60,${0.5 + 0.5 * Math.sin(t * 9)})`;
      c.lineWidth = 4;
      this.rr(r.x1 - 8, y0 - 8, r.x2 - r.x1 + 16, r.w + 16, 8);
      c.stroke();
      c.restore();
    }
    // Всегда видимый статус полосы: занята / съезд / риск
    this.drawRunwaySignal(r, t, y0);
  }

  /** Чип статуса на полосе (занятость, съезд, грязь). */
  private drawRunwaySignal(r: Runway, t: number, y0: number): void {
    const c = this.ctx;
    const occ = r.plane && !r.plane.dead ? r.plane : null;
    const ice = r.ice || 0;
    let lab = "";
    let bg = "rgba(12,22,36,.82)";
    let fg = "#dceaff";
    if (occ) {
      if (occ.status === "vacating") {
        lab = `СЪЕЗД · ${occ.cs}`;
        bg = "rgba(40,28,8,.88)";
        fg = "#ffd089";
      } else if (
        occ.status === "landing" ||
        occ.status === "inbound" ||
        occ.status === "takeoff"
      ) {
        lab =
          occ.status === "takeoff"
            ? `ВЗЛЕТ · ${occ.cs}`
            : `ЗАНЯТА · ${occ.cs}`;
        bg = "rgba(42,18,12,.88)";
        fg = "#ffb4a8";
      } else {
        lab = `ЗАНЯТА · ${occ.cs}`;
        bg = "rgba(42,18,12,.88)";
        fg = "#ffb4a8";
      }
    } else if (r.closed) {
      lab = "УБОРКА";
      bg = "rgba(42,28,8,.88)";
      fg = "#ffc861";
    } else if (r.snow > 72 || ice > 45) {
      lab =
        ice > 45
          ? `РИСК · лед ${Math.round(ice)}%`
          : `РИСК · снег ${Math.round(r.snow)}%`;
      bg = "rgba(48,12,14,.88)";
      fg = "#ff8e8e";
    } else if (r.snow > 62 || ice > 40) {
      lab =
        ice > 40
          ? `ГРЯЗЬ · лед ${Math.round(ice)}%`
          : `ГРЯЗЬ · снег ${Math.round(r.snow)}%`;
      bg = "rgba(40,24,8,.85)";
      fg = "#ffc861";
    }
    if (!lab) return;
    c.save();
    c.font = "800 12px ui-monospace,Menlo,monospace";
    c.textAlign = "center";
    const tw = c.measureText(lab).width + 14;
    const x = r.x1 + (r.x2 - r.x1) / 2;
    const y = y0 + r.w + 6;
    const pulse =
      occ || r.closed ? 0.85 + 0.15 * Math.sin(t * 5) : 1;
    c.globalAlpha = pulse;
    c.fillStyle = bg;
    this.rr(x - tw / 2, y, tw, 17, 5);
    c.fill();
    c.fillStyle = fg;
    c.fillText(lab, x, y + 12);
    c.restore();
  }

  /** Точки hold-short и борт, ждущий взлет. */
  private drawHoldShorts(g: GameState, t: number): void {
    const c = this.ctx;
    HOLD_SHORT.forEach((h, i) => {
      const waiting = g.planes.find(
        (p) =>
          !p.dead && p.status === "hold_short" && p.rwyWish === i,
      );
      c.save();
      c.strokeStyle = waiting
        ? `rgba(255,200,97,${0.55 + 0.35 * Math.sin(t * 4)})`
        : "rgba(160,190,220,.28)";
      c.lineWidth = waiting ? 2.6 : 1.6;
      c.setLineDash(waiting ? [8, 6] : [6, 8]);
      c.beginPath();
      c.arc(h.x, h.y, waiting ? 32 : 22, 0, 7);
      c.stroke();
      c.setLineDash([]);
      if (waiting) {
        c.fillStyle = "rgba(40,28,8,.86)";
        const lab = `ЖДЕТ ВЗЛЕТ · ${waiting.cs}`;
        c.font = "800 11px ui-monospace,Menlo,monospace";
        c.textAlign = "center";
        const tw = c.measureText(lab).width + 12;
        this.rr(h.x - tw / 2, h.y - 48, tw, 16, 5);
        c.fill();
        c.fillStyle = "#ffd089";
        c.fillText(lab, h.x, h.y - 37);
      } else {
        c.fillStyle = "rgba(160,190,220,.55)";
        c.font = "700 9px ui-monospace,Menlo,monospace";
        c.textAlign = "center";
        c.fillText("H/S", h.x, h.y + 3);
      }
      c.restore();
    });
  }

  private drawPlane(
    p: Plane,
    t: number,
    g: GameState,
    selected: Plane | null,
  ): void {
    const c = this.ctx;
    const sc = 1 + p.alt * 0.28;
    const L = p.T.len;
    const body = "#e9f1fb";
    const dark = "#9fb4cf";
    const acc = p.T.col;

    // Тень
    if (p.alt < 0.9) {
      c.save();
      c.globalAlpha = (1 - p.alt) * 0.45 * p.fade;
      c.translate(p.x + p.alt * 30, p.y + 12 + p.alt * 40);
      c.rotate(p.ang);
      c.scale(sc, sc);
      c.fillStyle = "#000";
      c.beginPath();
      c.ellipse(0, 0, L * 0.5, L * 0.16, 0, 0, 7);
      c.fill();
      c.beginPath();
      c.ellipse(-L * 0.08, 0, L * 0.16, L * 0.44, 0, 0, 7);
      c.fill();
      c.restore();
    }

    c.save();
    c.globalAlpha = p.fade;
    c.translate(p.x, p.y);
    c.rotate(p.ang);
    c.scale(sc, sc);

    // Посадочная фара
    if (
      p.status === "inbound" ||
      p.status === "landing" ||
      p.status === "takeoff"
    ) {
      c.save();
      c.globalCompositeOperation = "lighter";
      const beam = c.createLinearGradient(0, 0, 190, 0);
      beam.addColorStop(0, "rgba(220,240,255,.30)");
      beam.addColorStop(1, "rgba(220,240,255,0)");
      c.fillStyle = beam;
      c.beginPath();
      c.moveTo(L * 0.4, 0);
      c.lineTo(190, -46);
      c.lineTo(190, 46);
      c.closePath();
      c.fill();
      c.restore();
    }

    if (p.type === "regional") {
      c.fillStyle = dark;
      c.beginPath();
      c.moveTo(L * 0.1, -L * 0.05);
      c.lineTo(L * 0.02, -L * 0.52);
      c.lineTo(-L * 0.06, -L * 0.52);
      c.lineTo(-L * 0.04, -L * 0.05);
      c.closePath();
      c.fill();
      c.beginPath();
      c.moveTo(L * 0.1, L * 0.05);
      c.lineTo(L * 0.02, L * 0.52);
      c.lineTo(-L * 0.06, L * 0.52);
      c.lineTo(-L * 0.04, L * 0.05);
      c.closePath();
      c.fill();
      const sp = t * 26;
      for (const s of [-1, 1]) {
        c.save();
        c.translate(L * 0.05, s * L * 0.3);
        c.fillStyle = "#7f94ad";
        this.rr(-4, -4, 12, 8, 3);
        c.fill();
        c.strokeStyle = "rgba(200,225,255,.5)";
        c.lineWidth = 1.2;
        c.beginPath();
        c.moveTo(8, -7 * Math.sin(sp));
        c.lineTo(8, 7 * Math.sin(sp));
        c.stroke();
        c.restore();
      }
    } else if (p.type === "cargo") {
      c.fillStyle = dark;
      c.beginPath();
      c.moveTo(L * 0.14, -L * 0.06);
      c.lineTo(L * 0.0, -L * 0.56);
      c.lineTo(-L * 0.12, -L * 0.56);
      c.lineTo(-L * 0.06, -L * 0.06);
      c.closePath();
      c.fill();
      c.beginPath();
      c.moveTo(L * 0.14, L * 0.06);
      c.lineTo(L * 0.0, L * 0.56);
      c.lineTo(-L * 0.12, L * 0.56);
      c.lineTo(-L * 0.06, L * 0.06);
      c.closePath();
      c.fill();
      c.fillStyle = "#6d8299";
      for (const s of [-1, 1]) {
        this.rr(L * 0.0 - 6, s * L * 0.3 - 5, 16, 10, 4);
        c.fill();
        this.rr(L * 0.0 - 6, s * L * 0.46 - 4, 13, 8, 4);
        c.fill();
      }
    } else {
      c.fillStyle = dark;
      c.beginPath();
      c.moveTo(L * 0.16, -L * 0.05);
      c.lineTo(-L * 0.16, -L * 0.5);
      c.lineTo(-L * 0.28, -L * 0.48);
      c.lineTo(-L * 0.1, -L * 0.05);
      c.closePath();
      c.fill();
      c.beginPath();
      c.moveTo(L * 0.16, L * 0.05);
      c.lineTo(-L * 0.16, L * 0.5);
      c.lineTo(-L * 0.28, L * 0.48);
      c.lineTo(-L * 0.1, L * 0.05);
      c.closePath();
      c.fill();
      c.fillStyle = "#6d8299";
      for (const s of [-1, 1]) {
        this.rr(-L * 0.1, s * L * 0.26 - 5.5, 17, 11, 5);
        c.fill();
      }
    }

    // Хвостовое оперение
    c.fillStyle = dark;
    c.beginPath();
    c.moveTo(-L * 0.34, -L * 0.03);
    c.lineTo(-L * 0.46, -L * 0.26);
    c.lineTo(-L * 0.5, -L * 0.26);
    c.lineTo(-L * 0.44, -L * 0.03);
    c.closePath();
    c.fill();
    c.beginPath();
    c.moveTo(-L * 0.34, L * 0.03);
    c.lineTo(-L * 0.46, L * 0.26);
    c.lineTo(-L * 0.5, L * 0.26);
    c.lineTo(-L * 0.44, L * 0.03);
    c.closePath();
    c.fill();

    // Фюзеляж
    const fus = c.createLinearGradient(0, -L * 0.14, 0, L * 0.14);
    fus.addColorStop(0, "#ffffff");
    fus.addColorStop(0.5, body);
    fus.addColorStop(1, "#a9bdd6");
    c.fillStyle = fus;
    c.beginPath();
    c.ellipse(0, 0, L * 0.5, L * (p.type === "cargo" ? 0.14 : 0.115), 0, 0, 7);
    c.fill();

    // Киль
    c.fillStyle = acc;
    c.beginPath();
    c.moveTo(-L * 0.5, 0);
    c.lineTo(-L * 0.36, -L * 0.02);
    c.lineTo(-L * 0.4, -L * 0.04);
    c.lineTo(-L * 0.5, -L * 0.03);
    c.closePath();
    c.fill();
    c.fillStyle = acc;
    c.globalAlpha = p.fade * 0.85;
    c.fillRect(-L * 0.3, -L * 0.02, L * 0.55, L * 0.035);
    c.globalAlpha = p.fade;

    // Кабина
    c.fillStyle = "rgba(40,70,110,.85)";
    c.beginPath();
    c.ellipse(L * 0.38, 0, L * 0.07, L * 0.055, 0, 0, 7);
    c.fill();

    // Огни
    const strobe = p.strobe % 1.2 < 0.09;
    const wingY = p.type === "cargo" ? 0.56 : 0.5;
    this.lightDot(
      -L * 0.02,
      -L * wingY,
      1.7,
      "rgba(255,70,70,.95)",
      7,
      "rgba(255,60,60,.28)",
    );
    this.lightDot(
      -L * 0.02,
      L * wingY,
      1.7,
      "rgba(70,255,120,.95)",
      7,
      "rgba(60,255,110,.28)",
    );
    if (strobe) {
      this.lightDot(
        -L * 0.5,
        0,
        2.4,
        "rgba(255,255,255,.98)",
        14,
        "rgba(255,255,255,.4)",
      );
    }
    c.restore();

    // Подпись / выделение
    c.save();
    c.globalAlpha = p.fade;
    const sel = selected === p;
    const alarm =
      p.warn > 0 || g.hazards.some((h) => h.p === p || h.q === p);
    if (sel || alarm) {
      const rr0 = L * 0.62 + (sel ? 8 : 4) + Math.sin(t * 6) * 2.5;
      c.strokeStyle = alarm
        ? `rgba(255,70,70,${0.6 + 0.4 * Math.sin(t * 10)})`
        : "rgba(95,215,255,.9)";
      c.lineWidth = alarm ? 3 : 2.2;
      c.setLineDash(sel && !alarm ? [7, 6] : []);
      c.beginPath();
      c.arc(p.x, p.y, rr0, 0, 7);
      c.stroke();
      c.setLineDash([]);
      if (sel) {
        c.strokeStyle = "rgba(95,215,255,.35)";
        c.beginPath();
        c.arc(p.x, p.y, rr0 + 9, 0, 7);
        c.stroke();
      }
    }
    if (p.priority) {
      c.strokeStyle = `rgba(181,140,255,${0.5 + 0.4 * Math.sin(t * 4)})`;
      c.lineWidth = 2;
      c.beginPath();
      c.arc(p.x, p.y, L * 0.62 + 13, 0, 7);
      c.stroke();
    }
    const ty = p.y - L * 0.62 - 16 - p.alt * 8;
    c.font = "700 12px ui-monospace,Menlo,monospace";
    c.textAlign = "center";
    const tw = c.measureText(p.cs).width + 14;
    c.fillStyle = "rgba(6,14,26,.72)";
    this.rr(p.x - tw / 2, ty - 12, tw, 17, 5);
    c.fill();
    c.strokeStyle = sel
      ? "rgba(95,215,255,.8)"
      : "rgba(120,170,230,.25)";
    c.lineWidth = 1;
    this.rr(p.x - tw / 2, ty - 12, tw, 17, 5);
    c.stroke();
    c.fillStyle = alarm ? "#ff9b9b" : sel ? "#bfeaff" : "#cfe0f5";
    c.fillText(p.cs, p.x, ty + 1);
    if (
      ["approach", "awaiting_gate", "ready", "hold_short", "service"].includes(
        p.status,
      )
    ) {
      const bw = 40;
      const bx = p.x - bw / 2;
      const by = ty + 6;
      c.fillStyle = "rgba(255,255,255,.15)";
      this.rr(bx, by, bw, 3.6, 2);
      c.fill();
      const v = clamp(p.pat, 0, 1);
      c.fillStyle =
        p.status === "service"
          ? "#5fd7ff"
          : p.status === "approach"
            ? v > 0.5
              ? "#7ee08a"
              : v > 0.25
                ? "#ffc861"
                : "#ff6b6b"
            : // терпение экипажа — без зеленого «готов»
              v > 0.5
              ? "#8eb4d4"
              : v > 0.25
                ? "#ffc861"
                : "#ff6b6b";
      this.rr(bx, by, bw * v, 3.6, 2);
      c.fill();
    }
    c.restore();
  }

  private drawTargets(
    g: GameState,
    selected: Plane | null,
    t: number,
  ): void {
    if (!selected) return;
    const c = this.ctx;
    const p = selected;
    const pulse = 0.4 + 0.35 * Math.sin(t * 4);
    c.save();
    if (p.status === "approach") {
      g.runways.forEach((r) => {
        const check = canLand(p, r);
        if (!check.ok) return;
        c.strokeStyle = check.risky
          ? `rgba(255,107,107,${pulse})`
          : `rgba(95,215,255,${pulse})`;
        c.lineWidth = 3;
        c.setLineDash([18, 14]);
        this.rr(r.x1 - 6, r.y - r.w / 2 - 6, r.x2 - r.x1 + 12, r.w + 12, 10);
        c.stroke();
        c.setLineDash([]);
        const fafX = Math.max(120, r.x1 - 40);
        c.strokeStyle = "rgba(95,215,255,.45)";
        c.lineWidth = 2;
        c.setLineDash([9, 9]);
        c.beginPath();
        c.moveTo(p.x, p.y);
        c.lineTo(clamp(p.x, 160, 1400), Math.min(p.y, r.y - 70));
        c.lineTo(fafX, r.y - 16);
        c.lineTo(r.x1 + 90, r.y);
        c.stroke();
        c.setLineDash([]);
      });
    }
    if (p.status === "awaiting_gate" || p.status === "vacating") {
      for (const gt of g.gates) {
        if (gt.plane && gt.plane !== p) continue;
        c.strokeStyle = `rgba(126,224,138,${pulse})`;
        c.lineWidth = 3;
        c.setLineDash([12, 10]);
        c.beginPath();
        c.arc(gt.x, gt.y, 44, 0, 7);
        c.stroke();
        c.setLineDash([]);
      }
    }
    if (
      p.gate !== null &&
      (p.status === "taxi_gate" ||
        p.status === "service" ||
        p.status === "ready")
    ) {
      const gate = g.gates[p.gate];
      if (gate) {
        const color = p.status === "ready"
          ? [138, 236, 160]
          : p.status === "service"
            ? [255, 208, 120]
            : [110, 224, 255];
        c.strokeStyle = `rgba(${color[0]},${color[1]},${color[2]},${pulse})`;
        c.lineWidth = 3;
        c.setLineDash(p.status === "taxi_gate" ? [12, 9] : []);
        c.beginPath();
        c.arc(gate.x, gate.y, 48, 0, 7);
        c.stroke();
        c.setLineDash([]);
        c.fillStyle = `rgba(${color[0]},${color[1]},${color[2]},.9)`;
        c.font = "800 10px ui-monospace,Menlo,monospace";
        c.textAlign = "center";
        c.fillText(
          p.status === "taxi_gate"
            ? `НАЗНАЧЕН · ${gate.name}`
            : p.status === "service"
              ? `ОБСЛУЖИВАНИЕ · ${gate.name}`
              : `ГОТОВ · ${gate.name}`,
          gate.x,
          gate.y - 54,
        );
      }
    }
    if (p.status === "ready") {
      HOLD_SHORT.forEach((h, i) => {
        c.strokeStyle = `rgba(255,200,97,${pulse})`;
        c.lineWidth = 3;
        c.setLineDash([12, 10]);
        c.beginPath();
        c.arc(h.x, h.y, 34, 0, 7);
        c.stroke();
        c.setLineDash([]);
        c.fillStyle = "rgba(255,200,97,.8)";
        c.font = "700 11px ui-monospace,Menlo,monospace";
        c.textAlign = "center";
        c.fillText(g.runways[i]!.name, h.x, h.y - 42);
      });
    }
    if (p.status === "hold_short" && p.rwyWish != null) {
      const r = g.runways[p.rwyWish]!;
      const check = canTakeoff(g, p);
      c.strokeStyle = check.ok
        ? check.risky
          ? `rgba(255,107,107,${pulse})`
          : `rgba(126,224,138,${pulse})`
        : `rgba(255,107,107,${pulse})`;
      c.lineWidth = 3;
      c.setLineDash([18, 14]);
      this.rr(r.x1 - 6, r.y - r.w / 2 - 6, r.x2 - r.x1 + 12, r.w + 12, 10);
      c.stroke();
      c.setLineDash([]);
    }
    if (p.status === "takeoff" && p.path.length) {
      c.strokeStyle = "rgba(126,224,138,.55)";
      c.lineWidth = 2.4;
      c.setLineDash([8, 8]);
      c.beginPath();
      c.moveTo(p.x, p.y);
      for (const w of p.path) c.lineTo(w.x, w.y);
      c.stroke();
      c.setLineDash([]);
    }
    if (
      p.path.length &&
      (p.status === "taxi_gate" ||
        p.status === "taxi_rwy" ||
        p.status === "vacating" ||
        p.status === "awaiting_gate")
    ) {
      c.strokeStyle = "rgba(95,215,255,.5)";
      c.lineWidth = 2.4;
      c.setLineDash([10, 8]);
      c.beginPath();
      c.moveTo(p.x, p.y);
      for (const w of p.path) c.lineTo(w.x, w.y);
      c.stroke();
      c.setLineDash([]);
    }
    c.restore();
  }

  private drawClearingGear(g: GameState): void {
    const c = this.ctx;
    for (const r of g.runways) {
      if (!(r.clearSnowT > 0)) continue;
      const x = clamp(r.clearX || r.x1 + 36, r.x1 + 20, r.x2 - 20);
      const y = r.y;
      c.save();
      c.translate(x, y);
      c.fillStyle = "#ffc861";
      this.rr(-16, -9, 32, 18, 4);
      c.fill();
      c.fillStyle = "#1a2433";
      this.rr(-7, -6, 12, 12, 2);
      c.fill();
      c.restore();
      this.lightDot(
        x,
        y,
        3,
        "rgba(255,180,60,.9)",
        14,
        "rgba(255,170,40,.25)",
      );
    }
  }

  private drawWeather(g: GameState | null, dt: number): void {
    const c = this.ctx;
    const wind =
      Math.cos(g?.windDir ?? -0.55) * (g?.windSpd ?? 12) * 3.2;
    const dens = g
      ? frontStage(g).dens * g.front.intensity
      : 1;
    const intensity =
      1 + (g?.snowBoost ? 0.9 : 0) + (g?.vis ?? 0.1) + dens * 0.5;
    c.save();
    for (const f of this.flakes) {
      f.x += wind * (0.4 + f.z) * dt;
      f.y += f.s * dt * intensity;
      if (f.y > H + 8) {
        f.y = -8;
        f.x = Math.random() * W;
      }
      if (f.x > W + 8) f.x = -8;
      if (f.x < -8) f.x = W + 8;
      c.fillStyle = `rgba(226,240,255,${0.18 + f.z * 0.55})`;
      c.beginPath();
      c.arc(f.x, f.y, f.r * (0.7 + intensity * 0.25), 0, 7);
      c.fill();
    }
    c.restore();
    if (g && g.vis > 0.01) {
      c.fillStyle = `rgba(150,190,235,${g.vis * 0.42})`;
      c.fillRect(0, 0, W, H);
    }
    const vg = c.createRadialGradient(W / 2, H / 2, H * 0.35, W / 2, H / 2, H * 0.95);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,.55)");
    c.fillStyle = vg;
    c.fillRect(0, 0, W, H);
  }

  private drawFront(g: GameState, t: number): void {
    const c = this.ctx;
    const f = g.front;
    const st = frontStage(g);
    const grd = c.createLinearGradient(f.x - f.width / 2, 0, f.x + f.width / 2, 0);
    grd.addColorStop(0, "rgba(160,200,255,0)");
    grd.addColorStop(0.5, `rgba(180,210,255,${0.1 + st.dens * 0.1})`);
    grd.addColorStop(1, "rgba(160,200,255,0)");
    c.fillStyle = grd;
    c.fillRect(f.x - f.width / 2, 0, f.width, H);
    c.strokeStyle = `rgba(200,230,255,${0.35 + 0.25 * Math.sin(t * 3)})`;
    c.lineWidth = 2;
    c.setLineDash([10, 12]);
    c.beginPath();
    c.moveTo(f.x, 40);
    c.lineTo(f.x, H - 40);
    c.stroke();
    c.setLineDash([]);
    c.fillStyle = "rgba(200,230,255,.75)";
    c.font = "700 12px ui-monospace,monospace";
    c.textAlign = "center";
    c.fillText(
      `ФРОНТ · ${st.name}${g.cfg.endless ? ` · в${f.wave}` : ""}`,
      f.x,
      56,
    );
  }

  private drawFloats(g: GameState): void {
    const c = this.ctx;
    c.save();
    c.textAlign = "center";
    c.font = "800 15px ui-monospace,Menlo,monospace";
    for (const f of g.floats) {
      c.globalAlpha = clamp(f.t, 0, 1);
      c.fillStyle = "rgba(4,10,20,.6)";
      const w = c.measureText(f.txt).width + 12;
      this.rr(f.x - w / 2, f.y - 14, w, 19, 5);
      c.fill();
      c.fillStyle = f.col;
      c.fillText(f.txt, f.x, f.y);
    }
    c.restore();
  }

  private drawHazards(g: GameState, t: number): void {
    if (!g.hazards.length) return;
    const h = g.hazards[0]!;
    const c = this.ctx;
    c.save();
    c.globalAlpha = 0.55 + 0.45 * Math.sin(t * 9);
    c.fillStyle = "#ff5252";
    c.font = "800 26px ui-monospace,Menlo,monospace";
    c.textAlign = "center";
    const txt =
      h.kind === "closed"
        ? "КОНФЛИКТ: ПОЛОСА ЗАКРЫТА"
        : h.kind === "near"
          ? "ОПАСНОЕ СБЛИЖЕНИЕ"
          : "КРИТИЧЕСКИЙ ЗАНОС";
    c.fillText(`⚠ ${txt}`, W / 2, 224);
    c.restore();
    const w = 280;
    const x = W / 2 - w / 2;
    const y = 234;
    c.fillStyle = "rgba(255,255,255,.15)";
    this.rr(x, y, w, 6, 3);
    c.fill();
    c.fillStyle = "#ff5252";
    this.rr(x, y, w * clamp(h.t / h.max, 0, 1), 6, 3);
    c.fill();
  }

  private drawRadar(g: GameState | null, t: number): void {
    const el = this.rcv;
    const ctx = this.rctx;
    if (!el || !ctx) return;
    const dpr =
      (el as HTMLCanvasElement & { _dpr?: number })._dpr ||
      Math.min(window.devicePixelRatio || 1, 2);
    const css =
      (el as HTMLCanvasElement & { _css?: number })._css || el.width / dpr;
    const s = css;
    const cen = s / 2;
    const R = cen - 6;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, s, s);
    const bg = ctx.createRadialGradient(cen, cen, 4, cen, cen, R);
    bg.addColorStop(0, "rgba(28,70,95,.7)");
    bg.addColorStop(1, "rgba(6,18,30,.95)");
    ctx.fillStyle = bg;
    ctx.beginPath();
    ctx.arc(cen, cen, R, 0, 7);
    ctx.fill();
    ctx.strokeStyle = "rgba(110,224,255,.45)";
    ctx.beginPath();
    ctx.arc(cen, cen, R, 0, 7);
    ctx.stroke();
    const a = (t * 2.1) % (Math.PI * 2);
    ctx.save();
    ctx.translate(cen, cen);
    ctx.strokeStyle = "rgba(160,255,220,.95)";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(a) * R, Math.sin(a) * R);
    ctx.stroke();
    ctx.restore();
    if (!g) return;
    for (const p of g.planes) {
      if (p.dead) continue;
      const dx = (p.x - 800) / 900;
      const dy = (p.y - 420) / 620;
      let bx = cen + dx * R * 0.92;
      let by = cen + dy * R * 0.92;
      const d = Math.hypot(bx - cen, by - cen);
      if (d > R - 4) {
        const k = (R - 4) / d;
        bx = cen + (bx - cen) * k;
        by = cen + (by - cen) * k;
      }
      ctx.fillStyle = p.priority
        ? "#c4a0ff"
        : p.pat < 0.3
          ? "#ff7b7b"
          : "#8aeca0";
      ctx.beginPath();
      ctx.arc(bx, by, 3, 0, 7);
      ctx.fill();
    }
  }

  private windBearingLabel(a: number): string {
    const deg = (((a * 180) / Math.PI) % 360 + 360) % 360;
    const names = ["С", "СВ", "В", "ЮВ", "Ю", "ЮЗ", "З", "СЗ"];
    return names[Math.round(deg / 45) % 8]!;
  }

  private drawWind(g: GameState | null): void {
    const el = this.wcv;
    const ctx = this.wctx;
    if (!el || !ctx) return;
    const dpr =
      (el as HTMLCanvasElement & { _dpr?: number })._dpr ||
      Math.min(window.devicePixelRatio || 1, 2);
    const css =
      (el as HTMLCanvasElement & { _css?: number })._css || el.width / dpr;
    const s = css;
    const c = s / 2;
    const R = c - 8;
    const cross = !!(g && g.cross);
    const spd = g ? Math.round(g.windSpd) : 0;
    const dir = g?.windDir ?? 0;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, s, s);
    ctx.strokeStyle = cross
      ? "rgba(255,120,120,.55)"
      : "rgba(150,200,240,.4)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(c, c, R, 0, 7);
    ctx.stroke();
    ctx.fillStyle = "rgba(200,220,245,.85)";
    ctx.font = "700 10px monospace";
    ctx.textAlign = "center";
    ctx.fillText("С", c, 12);
    ctx.fillText("Ю", c, s - 4);
    ctx.textAlign = "left";
    ctx.fillText("З", 3, c + 3);
    ctx.textAlign = "right";
    ctx.fillText("В", s - 3, c + 3);
    ctx.save();
    ctx.translate(c, c);
    ctx.rotate(dir);
    const L = R * 0.7;
    const grd = ctx.createLinearGradient(-L, 0, L, 0);
    grd.addColorStop(0, "rgba(95,215,255,.3)");
    grd.addColorStop(1, cross ? "#ff7b7b" : "#6ee0ff");
    ctx.strokeStyle = grd;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(-L, 0);
    ctx.lineTo(L * 0.7, 0);
    ctx.stroke();
    ctx.fillStyle = cross ? "#ff7b7b" : "#6ee0ff";
    ctx.beginPath();
    ctx.moveTo(L, 0);
    ctx.lineTo(L * 0.58, -7);
    ctx.lineTo(L * 0.58, 7);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = "#f2f7ff";
    ctx.font = "800 14px monospace";
    ctx.textAlign = "center";
    ctx.fillText(String(spd), c, c + 18);
    ctx.fillStyle = cross ? "#ff9a9a" : "rgba(180,210,240,.75)";
    ctx.font = "700 8px monospace";
    ctx.fillText(cross ? "БОК" : "УЗЛ", c, c + 29);
    if (cross && g) {
      ctx.fillStyle = "rgba(255,120,120,.9)";
      ctx.font = "800 7px monospace";
      ctx.fillText(`+${Math.ceil(g.crossT || 0)}с`, c, c + 39);
    }
    const cap = document.getElementById("windCap");
    if (cap) {
      const br = this.windBearingLabel(dir);
      cap.textContent = cross
        ? `бок · ${spd} узл · ${br}`
        : `ветер · ${spd} · ${br}`;
      cap.style.color = cross ? "#ff9a9a" : "";
    }
  }
}
